import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowDown } = FiIcons;

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-accent-light/30 pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="space-y-6">
              <h1 className="text-4xl lg:text-6xl font-playfair font-bold leading-tight">
                <span className="bg-gradient-to-r from-accent-magenta via-accent-purple to-accent-lavender bg-clip-text text-transparent animate-gradient-shift bg-300%">
                  Better Sleep Starts Here.
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-700 font-raleway leading-relaxed">
                The real reason you can't sleep? Your brain's sleep system is off balance. We help you fix it for good, with neuroscience-backed cognitive sleep therapy that upgrades your brain, not just your bedtime.
              </p>
              
              <div className="text-lg text-gray-600 font-raleway space-y-4">
                <p>Tried everything—melatonin, magnesium, meditation apps, blackout curtains—and still wide awake at 2 AM?</p>
                <p>The problem isn't just sleep. It's your brain's executive functions—attention, working memory, self-regulation—that control it.</p>
                <p>Sleep deprivation disrupts these brain skills, causing a vicious cycle of restless nights and foggy days.</p>
                <p className="font-semibold text-accent-magenta">We break that cycle. For good.</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollToSection('contact')}
                className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-accent-magenta/90 transition-all duration-200 shadow-xl hover:shadow-2xl"
              >
                Book Your Wake-Up Call
              </button>
              
              <button
                onClick={() => scrollToSection('problem')}
                className="flex items-center justify-center gap-2 border-2 border-accent-magenta text-accent-magenta px-8 py-4 rounded-full font-semibold hover:bg-accent-magenta hover:text-white transition-all duration-200"
              >
                Learn More
                <SafeIcon icon={FiArrowDown} className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Right Column - Visual Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
              <img
                src="https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                alt="Person sleeping peacefully in a comfortable bed"
                className="w-full h-80 object-cover rounded-2xl"
              />
              
              {/* Floating Stats */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="absolute -top-4 -right-4 bg-accent-gold text-gray-900 px-6 py-4 rounded-2xl shadow-xl"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold">85%</div>
                  <div className="text-sm font-medium">Fall asleep faster</div>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="absolute -bottom-4 -left-4 bg-accent-lavender text-white px-6 py-4 rounded-2xl shadow-xl"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold">20%</div>
                  <div className="text-sm font-medium">Cognitive boost</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;